/**
 * Asset adapters for the zen viewer modal (FRONT-290).
 *
 * The inbox chat modal originally accepted only inbox feed items (cron-run
 * results and memory documents). The Files page shows two kinds of items —
 * the SAME memory documents, plus user-uploaded files — and must open them in
 * the same modal. These pure helpers translate Files-page items into modal
 * assets and keep the per-kind capability rules testable without rendering.
 */
import type { FeedItem } from '~/hooks/queries/use-feed';
import type { UnifiedFileItem } from '~/hooks/queries/use-files-unified';
import type { ChatThreadRow } from '~/hooks/queries/use-chats';
import type { ChatAttachment } from './chat-input';
import type { PlanSource } from '~/hooks/mutations/use-plan-mutations';
import type { ContainerFile } from '~/hooks/queries/use-work-files';
import type { FolderMemberWire } from '~/hooks/queries/use-folders';

/** A user-uploaded file (user_files row) viewed in the zen modal. */
export interface FileAssetItem {
  type: 'file';
  /**
   * Modal identity: drives the React mount key AND the per-asset chat session
   * id (`inbox-${id}`). Prefixed so it can never collide with feed-item ids
   * (`doc-<filename>` / cron-run ids).
   */
  id: string;
  /** Raw user_files UUID for API calls (content fetch, edit, delete, raw). */
  fileId: string;
  name: string;
  fileKind: 'text' | 'image' | 'pdf' | 'binary';
  description: string | null;
  /** Whether the row carries original bytes — gates the raw-download route. */
  hasBytes: boolean;
  timestamp: string;
  /** FRONT-2235: the upload's own creation time, for the modal byline's
   *  Created/Updated line. Absent on a `sessionToModalAsset` stub (there is
   *  no real row to read it from yet) — the modal's own live `useFileDetail`
   *  read corrects it the instant that request lands, same as `name`/`kind`. */
  createdAt?: string;
  /** FRONT-2235 review F2: the upload's own last-edit time — deliberately a
   *  SEPARATE field from `timestamp` above, which can be the file's chat-
   *  activity time rather than a real edit (see `FeedItem.timestamp`'s own
   *  doc). The byline reads this field (via `selectLiveItemDates`), never
   *  `timestamp`. Absent on a `sessionToModalAsset` stub for the same reason
   *  as `createdAt`. */
  updatedAt?: string;
  // FRONT-1453: see the identical field on `FeedItem` (use-feed.ts) — set
  // ONLY by `sessionToModalAsset` when this asset was rebuilt from a bare
  // session id rather than read live off the Files page/Inbox feed.
  reopenedFromThread?: true;
}

/**
 * An anchor-less web chat hosted in the zen modal (FRONT-518). Unlike the
 * document/file kinds it has no asset to read — the conversation IS the
 * content. `sessionId` is the raw `web:<uuid>` (NOT wrapped in `inbox-`), so it
 * joins to chat_messages and the chat_threads overlay directly. `id === sessionId`
 * so the React mount key changes when the open thread switches (remount).
 */
export interface ChatThreadAssetItem {
  type: 'chat';
  id: string;
  sessionId: string;
  title: string | null;
  /** Crumb suffix after "Chat ·" — omitted from the crumb when absent. */
  agentLabel?: string;
  timestamp: string;
  /**
   * FRONT-2522: the agent this chat is pinned to (direct agent chat), or
   * absent for an ordinary Eddy chat. Rides on the ASSET (not an `initial*`
   * modal prop) so it survives reopen and follows the asset on a thread
   * switch.
   */
  directAgent?: { id: string; name: string };
}

export type ModalAssetItem = FeedItem | FileAssetItem | ChatThreadAssetItem;

export function isFileAsset(item: ModalAssetItem): item is FileAssetItem {
  return item.type === 'file';
}

/**
 * FRONT-1453 review finding #4: was this asset rebuilt from a bare session id
 * (`sessionToModalAsset`) rather than read live off the Inbox feed or the
 * Files page? Shared by `deleteDescriptorFor` (suppresses the header Trash
 * icon) and the modal's own "Delete everything" footer button — both delete
 * the same underlying document/upload, so both need the same guard.
 */
export function isReopenedFromThread(item: ModalAssetItem): boolean {
  return !isChatAsset(item) && Boolean(item.reopenedFromThread);
}

export function isChatAsset(item: ModalAssetItem): item is ChatThreadAssetItem {
  return item.type === 'chat';
}

/**
 * Mint a fresh anchor-less chat (the launcher's "Start"). The backend mints the
 * chat_threads row on the first user message and the title after the first
 * assistant reply, so this carries only a placeholder title.
 */
export function newChatAsset(directAgent?: { id: string; name: string }): ChatThreadAssetItem {
  const sessionId = `web:${crypto.randomUUID()}`;
  return {
    type: 'chat',
    id: sessionId,
    sessionId,
    title: null,
    timestamp: new Date().toISOString(),
    ...(directAgent ? { directAgent } : {}),
  };
}

// FRONT-1029's `interviewNudgeToChatAsset` (nudge card → day-one session in
// the inbox modal) was removed by FRONT-1214/BUG-061: the nudge now
// navigates to the full onboarding surface instead, because the modal has
// no "Enter Eddy" gate and no guided-tour handoff.

/**
 * An existing thread row → the modal asset the row should open as
 * (FRONT-1453). A session reopened from the pill rail, the modal's own
 * "Chat activity" dropdown, the top-bar activity board, or the Chats page
 * used to be treated as an
 * anchor-less chat unconditionally — for a session that started life as an
 * Inbox document/upload (`inbox-doc-<filename>` / `inbox-file-<uploadId>`)
 * that dropped the reading pane and the asset context Eddy needs to answer
 * about it. Routing through `sessionToModalAsset` recovers the real asset
 * from the session id itself, so reopening restores it instead of a
 * context-less transcript.
 */
export function chatThreadToAsset(row: ChatThreadRow): ModalAssetItem {
  return sessionToModalAsset(
    row.sessionId,
    row.title,
    row.lastActiveAt ?? row.createdAt,
    row.directAgent ?? undefined,
  );
}

/**
 * FRONT-1453: the inverse of `fileItemSessionId` below (and of the backend's
 * `inbox-${item.id}` session convention) — given a session id, recover which
 * document/upload it names, if any. PURE. Only ever matches a session this
 * module itself would have minted for a document or upload:
 * `inbox-doc-<filename>` or `inbox-file-<uploadId>`. Everything else —
 * `inbox-<cronRunFeedItemId>` (a cron-run briefing, no `doc-`/`file-`
 * marker), a bare `web:<uuid>` chat, `inbox-` with nothing after it, a raw
 * uuid — falls through to `null` on purpose: there is no document/upload to
 * recover for any of those.
 *
 * NOT the same function as the backend's `parseInboxSessionId`
 * (`src/agents/chat-anchor.ts`), even though the two parse the identical
 * session-id shapes — named differently on purpose so the two are never
 * mistaken for interchangeable (FRONT-1453 review finding #15). Two real
 * differences: the backend ALSO resolves the `cron-run` shape (it needs the
 * job name for a title seed); this one deliberately does not, because a
 * cron-run briefing has no client-reconstructable body and always degrades
 * to today's transcript-only chat asset below — see `sessionToModalAsset`'s
 * own doc. And the discriminant key here is `type`, there it's `kind` — kept
 * as its own accident of history rather than "fixed" to match, since
 * unifying the two would invite exactly the false assumption ("same
 * function, just imported twice") this rename exists to prevent.
 */
export type ParsedInboxAssetSession =
  | { type: 'document'; filename: string }
  | { type: 'upload'; fileId: string };

const INBOX_PREFIX = 'inbox-';
const DOC_PREFIX = 'doc-';
const FILE_PREFIX = 'file-';

export function parseInboxAssetSessionId(sessionId: string): ParsedInboxAssetSession | null {
  if (!sessionId.startsWith(INBOX_PREFIX)) return null;
  const rest = sessionId.slice(INBOX_PREFIX.length);
  if (rest.startsWith(DOC_PREFIX)) {
    const filename = rest.slice(DOC_PREFIX.length);
    return filename ? { type: 'document', filename } : null;
  }
  if (rest.startsWith(FILE_PREFIX)) {
    const fileId = rest.slice(FILE_PREFIX.length);
    return fileId ? { type: 'upload', fileId } : null;
  }
  return null;
}

/**
 * FRONT-1453: resolve ANY session id — reopened from the activity board, the
 * pill rail, the modal's own "Chat activity" dropdown, or the Chats page —
 * into the asset the zen modal
 * should render. Pure client-side derivation, no new endpoint: a
 * document/upload session's id already carries everything needed to rebuild
 * the asset the modal opened it under in the first place.
 *
 * The document/upload branches build deliberately thin STUBS — just enough
 * for `documentToFeedItem`/`uploadToFileAsset` to mint the same modal/session
 * id the original open used. `inbox-chat-modal.tsx` always prefers its own
 * live query (`useDocumentDetail`/`useFileDetail`) for the body, and — per
 * FRONT-1453's live-preference extension — for name/kind/description/
 * hasBytes too, so the stub is corrected the instant that request lands.
 *
 * A cron-run briefing (or anything else `parseInboxAssetSessionId` can't
 * place) degrades to today's transcript-only `ChatThreadAssetItem` — its
 * body only exists server-side, so this is unchanged behaviour, not a
 * regression.
 *
 * FRONT-1453 review finding #4: every asset this function returns is marked
 * `reopenedFromThread` — it was rebuilt from a bare session id, not read live
 * off the Inbox feed or the Files page, so it never had its OWN delete
 * affordance to begin with. Reopening a document/upload conversation this way
 * (the pill rail, the modal's own "Chat activity" dropdown, or the Chats
 * list) still shows the reader and rides the asset as chat context — that's
 * the whole point of the rebuild — but the header Trash icon's
 * underlying-document/file delete stays
 * suppressed, since the caller already offers a chat-level "Move to Recycle
 * bin"/trash action for this exact row and the two are easy to confuse.
 */
export function sessionToModalAsset(
  sessionId: string,
  title: string | null,
  timestamp: string,
  directAgent?: { id: string; name: string },
): ModalAssetItem {
  const parsed = parseInboxAssetSessionId(sessionId);
  if (parsed?.type === 'document') {
    const asset = documentToFeedItem({
      filename: parsed.filename,
      title: title ?? parsed.filename,
      summary: '',
      updatedAt: timestamp,
    });
    // FRONT-2235 review F2: `timestamp` here is the reopened thread's own
    // last-activity time (see `chatThreadToAsset` above), never a real edit
    // time — there is no document row to read one from yet. Strip the
    // `updatedAt` `documentToFeedItem` mirrored it into so the byline shows
    // no date line at all until the modal's live `useDocumentDetail` fetch
    // resolves and supplies the genuine value (`createdAt` is already
    // omitted above for the same reason).
    delete asset.updatedAt;
    return { ...asset, reopenedFromThread: true };
  }
  if (parsed?.type === 'upload') {
    const asset = uploadToFileAsset({
      id: parsed.fileId,
      name: title ?? parsed.fileId,
      kind: 'binary',
      description: null,
      hasBytes: false,
      updatedAt: timestamp,
    });
    // See the document branch above — same reasoning.
    delete asset.updatedAt;
    return { ...asset, reopenedFromThread: true };
  }
  return {
    type: 'chat',
    id: sessionId,
    sessionId,
    title,
    timestamp,
    ...(directAgent ? { directAgent } : {}),
  };
}

/**
 * FRONT-2497: a Worth revisiting "unfinished business" row opens the chat
 * itself, never a document — the same chat asset the Chats list reopens.
 * Null for any other item, or a chat item with no session to open.
 */
export function openLoopChatToModalAsset(item: FeedItem): ModalAssetItem | null {
  if (item.type !== 'chat' || !item.sessionId) return null;
  return sessionToModalAsset(item.sessionId, item.title ?? null, item.timestamp);
}

type UnifiedDocument = Extract<UnifiedFileItem, { source: 'document' }>;
type UnifiedUpload = Extract<UnifiedFileItem, { source: 'upload' }>;

/**
 * Files-page document card → the same FeedItem the inbox feed builds for that
 * document. CRITICAL: the id MUST be `doc-<filename>` — identical to the
 * backend's buildDocumentFeedItems — so a document opened from Files binds to
 * the SAME chat session (`inbox-doc-<filename>`) as when opened from the
 * inbox, and the conversation carries across both surfaces.
 *
 * Parameter is deliberately narrowed to just the fields this mapping reads
 * (not the full `UnifiedDocument` shape) so a plain `DocumentMeta` hit — e.g.
 * the command palette's search result, which has no `source`/`sortDate` —
 * can be passed straight through (FRONT-874) without re-deriving this mapping
 * at the call site.
 */
export function documentToFeedItem(
  doc: Pick<UnifiedDocument, 'filename' | 'title' | 'summary' | 'updatedAt'> &
    Partial<Pick<UnifiedDocument, 'createdAt'>>,
): FeedItem {
  return {
    id: `doc-${doc.filename}`,
    type: 'document',
    jobId: doc.filename,
    jobName: doc.title,
    category: 'document',
    result: doc.summary ?? '',
    taskType: 'document',
    timestamp: doc.updatedAt,
    filename: doc.filename,
    title: doc.title,
    kind: 'document',
    ...(doc.createdAt ? { createdAt: doc.createdAt } : {}),
    // FRONT-2235 review F2: mirrors `doc.updatedAt` into the dedicated
    // `updatedAt` field, independent of `timestamp` above. Real for every
    // current caller (Files-page cards, containerFileToAsset,
    // folderMemberToAsset) EXCEPT `sessionToModalAsset`'s stub, which passes
    // thread-activity time here for `timestamp`'s sake and deliberately
    // strips this field back off afterwards — see that function's own
    // comment for why.
    updatedAt: doc.updatedAt,
  };
}

/**
 * Files-page upload card → modal asset. Parameter is narrowed to just the
 * fields this mapping reads (not the full `UnifiedUpload` shape) — same
 * reasoning as `documentToFeedItem`'s `Pick` above — so a differently-shaped
 * upload-like row (e.g. `containerFileToAsset` below, which has no
 * `contentType`/`sizeBytes`/`source`/`sortDate`) can be passed straight
 * through without re-deriving this mapping at the call site.
 */
export function uploadToFileAsset(
  upload: Pick<UnifiedUpload, 'id' | 'name' | 'kind' | 'description' | 'hasBytes' | 'updatedAt'> &
    Partial<Pick<UnifiedUpload, 'createdAt'>>,
): FileAssetItem {
  return {
    type: 'file',
    id: `file-${upload.id}`,
    fileId: upload.id,
    name: upload.name,
    fileKind: upload.kind,
    description: upload.description,
    hasBytes: upload.hasBytes,
    timestamp: upload.updatedAt,
    ...(upload.createdAt ? { createdAt: upload.createdAt } : {}),
    // FRONT-2235 review F2: see `documentToFeedItem`'s identical field —
    // same real-for-every-caller-except-the-stub reasoning.
    updatedAt: upload.updatedAt,
  };
}

/**
 * FRONT-1085: a Work container's attachment row → modal asset. THE
 * session-continuity contract: reuses `documentToFeedItem`/`uploadToFileAsset`
 * verbatim (never a parallel id-building scheme) so a file opened from a
 * project's Files panel binds to the exact SAME chat session
 * (`inbox-file-<uploadId>` / `inbox-doc-<filename>`) as opening the identical
 * item from the plain Files screen.
 *
 * The id passed through is `file.ref` — the underlying `user_files.id` /
 * `memory_documents.filename` — NEVER `file.id` (the `work_container_files`
 * attachment row's own id, which is meaningless outside this container and
 * would mint an unrelated, one-off chat session per attachment instead of
 * sharing the file's real conversation).
 */
export function containerFileToAsset(file: ContainerFile): FeedItem | FileAssetItem {
  if (file.source === 'document') {
    return documentToFeedItem({
      filename: file.ref,
      title: file.name,
      summary: file.description ?? '',
      // FRONT-2235 F7: `file.createdAt` is `null` when the target couldn't
      // be resolved (see `ContainerFileWire.createdAt`'s own doc) — never
      // substitute `attachedAt` for it, just omit it, same as the stub path.
      createdAt: file.createdAt ?? undefined,
      updatedAt: file.updatedAt,
    });
  }
  return uploadToFileAsset({
    id: file.ref,
    name: file.name,
    kind: file.kind,
    description: file.description,
    hasBytes: file.hasBytes,
    createdAt: file.createdAt ?? undefined,
    updatedAt: file.updatedAt,
  });
}

/**
 * FRONT-776: a Folder's member row → modal asset. Same session-continuity
 * contract as `containerFileToAsset`: reuses `documentToFeedItem`/
 * `uploadToFileAsset` verbatim so a file opened from a folder binds to the
 * exact SAME chat session (`inbox-file-<uploadId>` / `inbox-doc-<filename>`)
 * as opening the identical item from the plain Files screen or a Work
 * container's Files panel. `file.ref` is the underlying `user_files.id` /
 * `memory_documents.filename` — never `file.id` (the `file_folder_members`
 * row's own id, meaningless outside this folder).
 */
export function folderMemberToAsset(file: FolderMemberWire): FeedItem | FileAssetItem {
  if (file.source === 'document') {
    return documentToFeedItem({
      filename: file.ref,
      title: file.name,
      summary: file.description ?? '',
      // FRONT-2235 review F2/F7: same null-when-unavailable convention as
      // `containerFileToAsset` above — `file.createdAt` is `null` when the
      // target couldn't be resolved, never `attachedAt`.
      createdAt: file.createdAt ?? undefined,
      updatedAt: file.updatedAt,
    });
  }
  return uploadToFileAsset({
    id: file.ref,
    name: file.name,
    kind: file.kind,
    description: file.description,
    hasBytes: file.hasBytes,
    createdAt: file.createdAt ?? undefined,
    updatedAt: file.updatedAt,
  });
}

/**
 * Inbox feed file row (FRONT-705) → modal asset. The backend's file feed rows
 * carry the raw upload id in `fileId`; the modal id is rebuilt the SAME way
 * `uploadToFileAsset` builds it (`file-<uploadId>`) so opening the file from
 * the Inbox derives the identical session (`inbox-file-<uploadId>`) as opening
 * the same file from the Files page — the whole point of the continuity
 * contract (a file's chat is one conversation no matter where it's opened).
 */
export function feedFileToFileAsset(item: FeedItem): FileAssetItem {
  const fileId = item.fileId ?? '';
  return {
    type: 'file',
    id: `file-${fileId}`,
    fileId,
    name: item.jobName || item.title || 'File',
    fileKind: item.fileKind ?? 'binary',
    description: item.description ?? null,
    hasBytes: item.hasBytes ?? false,
    timestamp: item.timestamp,
    ...(item.createdAt ? { createdAt: item.createdAt } : {}),
    // FRONT-2235 review F2: the file's real edit time, distinct from
    // `timestamp` above (this item's chat-activity time) — see `FeedItem
    // .updatedAt`'s own doc.
    ...(item.updatedAt ? { updatedAt: item.updatedAt } : {}),
  };
}

/**
 * FRONT-705: the chat session id a Files-page item opens under —
 * `inbox-<modalAssetId>`, derived through the SAME id builders the Files route
 * (`openItem`) and the modal (`inbox-${item.id}`) use. So the Files status dot
 * reads its status by the exact session the modal writes to and can never
 * drift: an upload → `inbox-file-<uploadId>`, a document → `inbox-doc-<filename>`.
 * (Coding-session documents open the coding window, not a chat, so they simply
 * have no chat status — the lookup returns nothing and no dot shows.)
 */
export function fileItemSessionId(item: UnifiedFileItem): string {
  const assetId =
    item.source === 'upload'
      ? uploadToFileAsset(item).id
      : documentToFeedItem(item).id;
  return `inbox-${assetId}`;
}

/**
 * Only plain text/markdown uploads are inline-editable. PDF "content" is
 * extracted text (editing it would not change the PDF), and image/binary
 * uploads have no editable text at all.
 */
export function canEditUpload(kind: FileAssetItem['fileKind']): boolean {
  return kind === 'text';
}

/**
 * Where a download must come from. The raw route 404s for rows without bytes
 * (markdown notes created in the app store text only), so those are
 * downloaded as a client-side blob of the content instead.
 */
export function uploadDownloadMode(asset: Pick<FileAssetItem, 'hasBytes'>): 'raw' | 'blob' {
  return asset.hasBytes ? 'raw' : 'blob';
}

export function rawFileUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/raw`;
}

/**
 * Extensions the sandbox's own `EXTENSION_CONTENT_TYPES` map
 * (`src/tools/custom-tool-outputs.ts`) types as `text/*` or
 * `application/json`. A file Eddy produces in its sandbox is stored WITH
 * bytes, so `src/files/kind.ts` classifies it `binary` even when it's plain
 * text — this list is what tells `opensAsDownload` apart from a genuinely
 * opaque binary. Kept in sync by hand: `src/files/kind.ts` is intentionally
 * not imported here (it never sees a filename, only a content type).
 */
const TEXT_LIKE_EXTENSIONS = new Set(['csv', 'json', 'txt', 'md', 'html']);

/**
 * Extensions the server extracts readable text from at save/upload time
 * (`src/files/extract.ts`'s `OOXML_EXTRACTORS` — docx, xlsx — plus its RTF
 * branch), mirroring `TEXT_LIKE_EXTENSIONS` above for the same reason: a
 * `binary`-classified row (bytes kept) can still carry real extracted text.
 * FRONT-2365: a tool-made Word/Excel/RTF file used to have none (its
 * extraction only ran on upload, not on a tool's save), so it downloaded
 * like an opaque binary even after the fix backfilled its text on read —
 * `opensAsDownload` never knew to look. `pptx` is deliberately NOT extracted
 * (no extractor exists for it) and keeps downloading.
 */
const EXTRACTED_TEXT_EXTENSIONS = new Set(['docx', 'xlsx', 'rtf']);

/**
 * True only for a kind the app has no reader for (FRONT-1934): a click on
 * such a row should download the file directly via `rawFileUrl` rather than
 * opening the zen viewer, which for one of these has no preview of the file
 * itself to show — only a download link plus the composer to chat about it
 * (`inbox-chat-modal.tsx` always renders that composer alongside a file
 * asset, readable or not). Every other kind (text/image/pdf) keeps opening
 * the reader — including a `binary`-classified file whose NAME extension is
 * text-like (`TEXT_LIKE_EXTENSIONS`, a sandbox-produced text file the
 * classifier had no way to tell apart from real opaque bytes, FRONT-1934
 * rule refinement) or extractor-backed (`EXTRACTED_TEXT_EXTENSIONS`, a
 * tool-made Word/Excel/RTF file, FRONT-2365). A name with no extension is
 * treated as neither, so it still downloads.
 * The single rule every row that makes this open-vs-download decision reads
 * — `files-list.tsx`'s `FileRow`/`FolderRow`, `folder-member-row.tsx`'s
 * `FolderMemberRow`, and the Files-screen upload card (`routes/files.tsx`)
 * — so it can't drift between call sites.
 */
export function opensAsDownload(file: { kind: FileAssetItem['fileKind']; name: string }): boolean {
  if (file.kind !== 'binary') return false;
  const dot = file.name.lastIndexOf('.');
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  return !TEXT_LIKE_EXTENSIONS.has(ext) && !EXTRACTED_TEXT_EXTENSIONS.has(ext);
}

/**
 * Uploads whose body is (extracted) text can be saved as a memory document /
 * fed to chat-context meaningfully; images and binaries cannot.
 */
export function uploadHasTextBody(kind: FileAssetItem['fileKind']): boolean {
  return kind === 'text' || kind === 'pdf';
}

/**
 * The chat-view body decision for a `binary` file asset (FRONT-2365): a
 * tool-made Word/Excel/RTF file keeps its bytes (so `fileKind` still calls
 * it `binary`) but, once healed on read (`GET /api/files/:id` runs
 * `ensureFileText` server-side), its detail row's `content` carries real
 * extracted text — the same shape a `kind: 'pdf'` row already renders text
 * for. An opaque binary with no extractor (a `.zip`, a `.pptx`) has empty
 * content and this stays `false`, keeping today's download-link-only
 * rendering.
 *
 * Two call sites in `inbox-chat-modal.tsx` (FRONT-2365 round 4), both gated
 * on `liveFileKind === 'binary'` and both reading the same `bodyText` /
 * `content` field — never a hand-built value — so neither is a parallel
 * guess at the other's answer: the asset-reader branch (does this binary
 * render its text or just a download link?) and the "Save as document"
 * footer button's eligibility check (can this binary be copied into a
 * document at all?).
 */
export function binaryAssetHasBody(content: string): boolean {
  return content.length > 0;
}

/**
 * The anchored file of a file-asset chat, expressed as a chat attachment so it
 * rides every turn the same way a composer upload does (FRONT-577).
 *
 * Why this exists: the modal used to hand the model only the file's *extracted
 * text* (as assetContext). Images are stored with no extracted text at all,
 * and even though a PDF's text layer is now also extracted (FRONT-1608),
 * text-only would still lose everything a native document block gives the
 * model (layout, embedded images, scanned pages) — both are meant to be read
 * natively as document/image content blocks, not from extracted text alone —
 * so on this surface the file was effectively invisible to the model. Sending
 * it as a real attachment routes it through the same server-side injection
 * the composer uses: PDFs/images become native media blocks, text/spreadsheets
 * get inlined. Only the fileId actually drives the injection (the server
 * re-reads the row), so the contentType here is cosmetic.
 */
export function fileAssetToContextAttachment(
  item: FileAssetItem,
  contentType: string,
): ChatAttachment {
  return {
    fileId: item.fileId,
    name: item.name,
    contentType,
    kind: item.fileKind,
  };
}

/** What the modal's header Trash icon deletes for a given asset (FRONT-861). */
export interface DeleteDescriptor {
  kind: 'document' | 'file';
  label: string;
  /** The API identifier the delete call needs — a filename for documents, the raw fileId for uploads. */
  identifier: string;
}

/**
 * Only documents and uploaded files have a persistent asset to delete — a
 * cron-run feed item and the anchor-less chat variant have nothing to trash,
 * so those return null and the header icon stays hidden. Mirrors the same
 * per-kind checks the modal itself uses to derive `isFile`/`isDocument`.
 *
 * FRONT-1453 review finding #4: also null for any asset `sessionToModalAsset`
 * rebuilt from a bare session id (`item.reopenedFromThread`) — reopening a
 * document/upload conversation from the pill rail, the modal's own "Chat
 * activity" dropdown, or the Chats list must not expose a MORE destructive
 * action (delete the
 * underlying document/file) than the chat-level "Move to Recycle bin"/trash
 * the caller already offers for that same row; the two read as near-synonyms
 * to someone who thinks they just opened a chat.
 */
export function deleteDescriptorFor(item: ModalAssetItem): DeleteDescriptor | null {
  if (isChatAsset(item)) return null;
  if (isReopenedFromThread(item)) return null;
  if (isFileAsset(item)) {
    // No fileId → nothing to call DELETE on; hide the icon rather than firing
    // a request against an empty id (would 404 silently).
    if (!item.fileId) return null;
    return { kind: 'file', label: 'Delete file', identifier: item.fileId };
  }
  if (item.type === 'document' && item.filename) {
    return { kind: 'document', label: 'Delete document', identifier: item.filename };
  }
  return null;
}

/** What the inbox reader's non-chat "Create project" button needs: the
 *  `PlanSource` to send and a human label for the dialog's "From this …:"
 *  line. */
export interface PlanFooterSource {
  source: PlanSource;
  label: string;
}

/**
 * FRONT-1122: derive the non-chat resolve footer's "Create project" source
 * from the same per-kind variables the modal already computes
 * (`isFileAsset`/`feedItem?.type`) — mirrors the checks `inbox-chat-modal.tsx`
 * uses to derive `isFile`/`isDocument`/`title`. Returns null when no usable
 * source can be derived (a file asset with no `fileId`) so the caller renders
 * no button rather than a broken one, same convention as `deleteDescriptorFor`
 * above.
 *
 * Reads the cron-run id off `feedItem.source.runId`, NOT the top-level
 * `feedItem.runId` — the top-level field is owner-only (it gates the feed
 * row's delete/select affordance, FRONT-191), which used to make this
 * footer's button silently disappear on an org-shared briefing the viewer
 * could read but didn't own. `source.runId` is present on every visible
 * cron-run item, own or shared, and the backend re-checks visibility on read.
 */
export function derivePlanSourceForFooter(args: {
  fileAsset: Pick<FileAssetItem, 'fileId' | 'name'> | null;
  feedItem: Pick<FeedItem, 'type' | 'filename' | 'title' | 'jobName' | 'source'> | null;
}): PlanFooterSource | null {
  const { fileAsset, feedItem } = args;
  if (fileAsset) {
    if (!fileAsset.fileId) return null;
    return { source: { kind: 'file', fileId: fileAsset.fileId }, label: fileAsset.name };
  }
  if (feedItem?.type === 'document' && feedItem.filename) {
    return {
      source: { kind: 'document', filename: feedItem.filename },
      label: feedItem.title || feedItem.jobName || feedItem.filename,
    };
  }
  if (feedItem?.type === 'cron-run' && typeof feedItem.source?.runId === 'number') {
    return {
      source: { kind: 'cron-run', runId: feedItem.source.runId },
      label: feedItem.title || feedItem.jobName || 'Briefing',
    };
  }
  return null;
}

/**
 * The reader's displayed name, most-current source first (FRONT-1107; PR #1030
 * review finding). PURE — the caller resolves each candidate, this only orders
 * them, so the precedence is stated once and readable in one line each:
 *
 *  1. `renamedName` — a rename the user just made in the open reader. The
 *     parent page hands the modal an `item` SNAPSHOT it never re-reads, so
 *     nothing else would repaint it.
 *  2. `chatTitle` — a chat asset's own thread title.
 *  3. `fileName` / `documentTitle` — the LIVE detail row for an upload or a
 *     document, which may have been renamed elsewhere (another tab, the Files
 *     card, an agent) since the list loaded, or whose snapshot may be a thin
 *     stub rebuilt from a bare session id (FRONT-1453). For uploads this
 *     matters beyond cosmetics: the name is part of what Save writes, so a
 *     stale one here is how a body-only edit would silently rename the file
 *     back.
 *  4. `snapshotTitle` — the snapshot, the only source left before anything
 *     live has loaded (or, for a cron-run item, ever — it has no detail route).
 *
 * Each candidate is `null` when its source does not apply, so the chain is a
 * plain nullish fallthrough rather than the nested conditional this replaced.
 * The final default is unreachable wherever a feed item exists (`jobName` is
 * required), and mirrors the chat branch's own default when one does not.
 */
export function resolveDisplayTitle(candidates: {
  renamedName: string | null;
  chatTitle: string | null;
  fileName: string | null | undefined;
  documentTitle: string | null;
  snapshotTitle: string | null;
}): string {
  return (
    candidates.renamedName ??
    candidates.chatTitle ??
    candidates.fileName ??
    candidates.documentTitle ??
    candidates.snapshotTitle ??
    'New chat'
  );
}
